// Conteúdo da documentação da ApiForClient (contrato em
// dashboard-clientes-backend/functions/src/apiForClient/README.md — manter igual).
//
// Dois perfis: 'franquia' (qualquer ops que não seja a muve) e 'muve'. O texto
// do perfil franquia não cita a operação-mãe nem outras operações, e o da muve
// não traz o que é só das franquias (ifoodUuid, tipoEmissaoNf, notas fiscais).

export type Perfil = 'franquia' | 'muve';

export const perfilDaOps = (currentOps: string | null): Perfil =>
  currentOps === 'muve' ? 'muve' : 'franquia';

export interface Campo {
  nome: string;
  tipo: string;
  obrigatorio: boolean;
  descricao: string;
}

export interface Endpoint {
  id: SecaoId;
  titulo: string;
  metodo: 'POST';
  caminho: string;
  descricao: string;
  body: Campo[];
  exemploBody: Record<string, unknown>;
  exemploResposta: unknown;
  observacoes?: string[];
}

export const SECOES = [
  { id: 'visao-geral', titulo: 'Visão geral' },
  { id: 'autenticacao', titulo: 'Autenticação' },
  { id: 'antecipacoes', titulo: 'Antecipações do dia' },
  { id: 'entregadores', titulo: 'Entregadores e bloqueios' },
  { id: 'bloqueios', titulo: 'Bloquear e desbloquear' },
  { id: 'financeiro', titulo: 'Relatório financeiro' },
  { id: 'webhook', titulo: 'Webhook' },
  { id: 'erros', titulo: 'Erros' },
] as const;

export type SecaoId = (typeof SECOES)[number]['id'];

export const TOKEN_EXPIRA_SEGUNDOS = 1800;

const antecipacaoExemplo = (perfil: Perfil) => ({
  id: '123',
  cnpjCpf: '12345678901',
  nomeCompleto: 'Maria Silva',
  telefone: '5511999990000',
  ...(perfil === 'franquia' && { ifoodUuid: 'c0a8012e-0000-0000-0000-000000000000' }),
  valorFace: 15000,
  valorCompra: 14550,
  dataRomaneio: '2026-10-27',
  dataVencimento: '2026-11-10',
  criadoEm: '2026-10-28 09:14:03',
  liquidado: false,
});

export const endpoints = (perfil: Perfil): Endpoint[] => {
  const franquia = perfil === 'franquia';

  return [
    {
      id: 'antecipacoes',
      titulo: 'Antecipações do dia',
      metodo: 'POST',
      caminho: '/v1/antecipacoes',
      descricao:
        'Lista as antecipações criadas no dia (horário de Brasília) pelos entregadores da sua operação, com o resumo de valores.',
      body: [
        {
          nome: 'data',
          tipo: 'string (YYYY-MM-DD)',
          obrigatorio: false,
          descricao: 'Dia da criação das antecipações. Padrão: hoje.',
        },
      ],
      exemploBody: { data: '2026-10-28' },
      exemploResposta: {
        data: '2026-10-28',
        summary: { quantidade: 1, valorFace: 15000, valorCompra: 14550 },
        antecipacoes: [antecipacaoExemplo(perfil)],
      },
      observacoes: [
        'valorFace é o valor do romaneio; valorCompra é o valor pago ao entregador.',
        'Para receber as antecipações sem consultar este endpoint, use o webhook.',
      ],
    },
    {
      id: 'entregadores',
      titulo: 'Entregadores e bloqueios',
      metodo: 'POST',
      caminho: '/v1/entregadores',
      descricao:
        'Lista os entregadores cadastrados na sua operação e se cada um está bloqueado para receber ofertas de antecipação.',
      body: [
        {
          nome: 'documento',
          tipo: 'string',
          obrigatorio: false,
          descricao: 'CPF ou CNPJ (com ou sem pontuação) para filtrar um entregador.',
        },
        {
          nome: 'somenteBloqueados',
          tipo: 'boolean',
          obrigatorio: false,
          descricao: 'true para trazer só os bloqueados.',
        },
        {
          nome: 'page',
          tipo: 'integer',
          obrigatorio: false,
          descricao: 'Página, a partir de 1. Padrão: 1.',
        },
        {
          nome: 'limit',
          tipo: 'integer',
          obrigatorio: false,
          descricao: 'Itens por página. Padrão: 100, máximo: 500.',
        },
      ],
      exemploBody: { somenteBloqueados: true, page: 1, limit: 100 },
      exemploResposta: {
        entregadores: [
          {
            numeroDocumento: '12345678901',
            nomeCompleto: 'Maria Silva',
            telefone: '5511999990000',
            ...(franquia && {
              ifoodUuid: 'c0a8012e-0000-0000-0000-000000000000',
              tipoEmissaoNf: 'MEI',
            }),
            bloqueado: true,
            bloqueio: {
              origem: 'cliente',
              motivoCodigo: 'conduta',
              motivoTexto: null,
              bloqueadoEm: '2026-10-20T13:00:00.000Z',
            },
          },
        ],
        pagination: { page: 1, limit: 100, total: 1, totalPages: 1 },
      },
      observacoes: [
        'bloqueio é null quando o entregador não está bloqueado.',
        'origem: "cliente" (bloqueio feito pela sua operação) ou "gestor" (feito pela Planalto). Do bloqueio feito pela Planalto vêm só a origem e o motivo.',
        'Para bloquear ou desbloquear, use POST /v1/entregadores/bloqueio.',
      ],
    },
    {
      id: 'bloqueios',
      titulo: 'Bloquear e desbloquear',
      metodo: 'POST',
      caminho: '/v1/entregadores/bloqueio',
      descricao:
        'Bloqueia ou desbloqueia entregadores da sua operação. Entregador bloqueado deixa de receber ofertas de antecipação. Mesmas regras da aba Bloqueios da dashboard.',
      body: [
        {
          nome: 'acao',
          tipo: 'string',
          obrigatorio: true,
          descricao: '"bloquear" ou "desbloquear".',
        },
        {
          nome: 'cpfs',
          tipo: 'string[]',
          obrigatorio: false,
          descricao:
            'CPFs (11 dígitos, com ou sem pontuação), como texto. Bloquear um CPF bloqueia todos os cadastros da pessoa, inclusive os de CNPJ.',
        },
        {
          nome: 'cnpjs',
          tipo: 'string[]',
          obrigatorio: false,
          descricao:
            'CNPJs (14 caracteres, com ou sem pontuação, numérico ou alfanumérico), como texto. Bloquear um CNPJ bloqueia só o cadastro daquele CNPJ.',
        },
        {
          nome: 'motivoCodigo',
          tipo: 'string',
          obrigatorio: false,
          descricao:
            'Só para bloquear: "desligado" (desligado da operação), "conduta", "pedido_operacao" (pedido da operação) ou "outro".',
        },
        {
          nome: 'motivoTexto',
          tipo: 'string',
          obrigatorio: false,
          descricao: 'Descrição livre. Obrigatória quando motivoCodigo é "outro".',
        },
      ],
      exemploBody: {
        acao: 'bloquear',
        cpfs: ['529.982.247-25'],
        cnpjs: ['11.222.333/0001-81'],
        motivoCodigo: 'conduta',
      },
      exemploResposta: {
        acao: 'bloquear',
        sucesso: [
          { tipo: 'cpf', documento: '52998224725' },
          { tipo: 'cnpj', documento: '11222333000181' },
        ],
        falhas: [],
        avisos: [],
      },
      observacoes: [
        'Informe ao menos um documento em cpfs ou cnpjs, até 5000 no total por chamada.',
        'Envie cada documento como texto ("01234567890"): como número, o zero à esquerda se perde.',
        'Cada documento é conferido antes de gravar (tamanho e dígito verificador). CPF enviado em cnpjs, ou CNPJ em cpfs, não é gravado e volta em falhas com a indicação do campo certo.',
        'sucesso traz os documentos bloqueados (ou desbloqueados); falhas, os que não mudaram e o motivo; avisos, os que mudaram mas merecem atenção. Cada item diz o tipo ("cpf" ou "cnpj").',
        'Bloquear um documento sem cadastro na sua operação funciona (fica valendo se ele se cadastrar), mas volta em avisos: confira se não foi digitado errado.',
        'A API só desbloqueia o que a sua operação bloqueou. Bloqueio feito pela Planalto (origem "gestor") continua valendo e volta em falhas ou avisos com a mensagem "Bloqueado pelo gestor".',
        'Repetir a mesma chamada é seguro: o que já estava bloqueado (ou desbloqueado) volta em falhas e nada muda.',
      ],
    },
    {
      id: 'financeiro',
      titulo: 'Relatório financeiro',
      metodo: 'POST',
      caminho: '/v1/financeiro',
      descricao: franquia
        ? 'Fechamento semanal (segunda a domingo): resumo por entregador, operações com a nota fiscal de cada uma, saldo remanescente da semana e o link do ZIP com todas as notas fiscais.'
        : 'Fechamento semanal (segunda a domingo): resumo por entregador e as operações da semana.',
      body: [
        {
          nome: 'segunda',
          tipo: 'string (YYYY-MM-DD)',
          obrigatorio: false,
          descricao: 'Segunda-feira da semana. Padrão: a semana anterior.',
        },
        {
          nome: 'allowCurrentWeek',
          tipo: 'boolean',
          obrigatorio: false,
          descricao:
            'true para consultar a semana em andamento (dados parciais). Sem ele, a semana atual é recusada.',
        },
      ],
      exemploBody: { segunda: '2026-10-19' },
      exemploResposta: {
        periodo: { segunda: '2026-10-19', domingo: '2026-10-25' },
        isCurrentWeek: false,
        semanaCompleta: true,
        diasFaltantes: [],
        resumo: [
          {
            cnpjCpf: '12345678901',
            nomeCompleto: 'Maria Silva',
            cpf: '12345678901',
            telefone: '5511999990000',
            valorFaceAgregado: 45000,
          },
        ],
        operacoes: [
          {
            cnpjCpf: '12345678901',
            telefone: '5511999990000',
            valorFace: 15000,
            valorCompra: 14550,
            dataRomaneio: '2026-10-20',
            dataVencimento: '2026-11-03',
            dataSolicitado: '2026-10-21 08:02:11',
            nomeCompleto: 'Maria Silva',
            cnpj: null,
            cpf: '12345678901',
            comprovanteBancario: 'https://...',
            ...(franquia && {
              tipoEmissaoNf: 'MEI',
              chaveNfs: '35260...',
              urlNfs: 'https://www.nfse.gov.br/ConsultaPublica/Download/DANFSe?chave=35260...',
            }),
          },
        ],
        ...(franquia && {
          saldoRemanescente: [
            {
              solId: '98765',
              cnpjCpf: '12345678901',
              cpf: '12345678901',
              nome: 'Maria Silva',
              telefone: '5511999990000',
              ifoodUuid: 'c0a8012e-0000-0000-0000-000000000000',
              tipoEmissaoNf: 'MEI',
              tomadorCnpj: '12345678000199',
              totalSemanalSemGorjeta: 120000,
              gorjeta: 8000,
              valorAntecipadoComNfs: 30000,
              valorAntecipadoEmitirNfs: 15000,
              saldoRemanescenteEmitirNfs: 75000,
              dataPeriodoInicio: '2026-10-19',
              dataPeriodoFim: '2026-10-25',
              chaveNfs: null,
              urlNfs: null,
            },
          ],
          nfsZip: {
            status: 'pronto',
            total: 42,
            url: 'https://...',
            urlExpiraEmSegundos: 900,
          },
        }),
      },
      observacoes: [
        'semanaCompleta é false quando há dias da semana sem antecipações compradas (listados em diasFaltantes).',
        'comprovanteBancario é o link do comprovante do PIX pago ao entregador; null enquanto o pagamento não foi feito.',
        ...(franquia
          ? [
              'saldoRemanescente: um registro por entregador com o total da semana sem gorjeta, a gorjeta, o que já foi antecipado (com e sem nota fiscal) e o saldo que ainda precisa de nota fiscal (saldoRemanescenteEmitirNfs, 0 quando a nota do saldo já foi emitida). tomadorCnpj é o CNPJ que vai como tomador na nota.',
              'nfsZip.status: "pronto" (url válida por 15 minutos), "gerando" (chame de novo em alguns minutos), "notas_pendentes" (ainda há notas sendo baixadas), "sem_notas" ou "semana_em_aberto".',
              'nfsZip.url é um link assinado para baixar o ZIP com todas as notas fiscais da semana. Para obtê-lo, chame este endpoint até o status ser "pronto" e use a url antes de expirar.',
            ]
          : []),
      ],
    },
  ];
};

export const exemploToken = {
  access_token: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...',
  token_type: 'Bearer',
  expires_in: TOKEN_EXPIRA_SEGUNDOS,
};

export const errosToken: { status: number; error: string; quando: string }[] = [
  { status: 400, error: 'unsupported_grant_type', quando: 'grant_type diferente de client_credentials.' },
  { status: 400, error: 'invalid_request', quando: 'Faltou client_id ou client_secret, ou o corpo é inválido.' },
  { status: 401, error: 'invalid_client', quando: 'client_id ou client_secret errado, ou credencial revogada.' },
];

export const erros: { status: number; quando: string }[] = [
  { status: 400, quando: 'Corpo ou campo inválido (data fora do formato, segunda que não é segunda-feira...).' },
  { status: 401, quando: 'Token ausente, inválido ou expirado, ou credencial revogada. Peça um token novo.' },
  { status: 403, quando: 'A credencial não tem mais acesso à API. Fale com a Planalto.' },
  { status: 500, quando: 'Erro interno. Tente novamente; se persistir, fale com a Planalto.' },
];

export const exemploWebhook = (perfil: Perfil) => ({
  evento: 'antecipacoes',
  data: '2026-10-28',
  enviadoEm: '2026-10-28T12:02:00.000Z',
  antecipacoes: [antecipacaoExemplo(perfil)],
});

export const headersWebhook: { nome: string; descricao: string }[] = [
  { nome: 'Content-Type', descricao: 'application/json' },
  { nome: 'X-Planalto-Timestamp', descricao: 'Momento do envio, em segundos desde 1970 (epoch).' },
  {
    nome: 'X-Planalto-Assinatura',
    descricao: 'sha256= seguido do HMAC-SHA256, em hexadecimal, de "<timestamp>.<corpo>" com o segredo do webhook.',
  },
];

export const verificacaoNode = `const crypto = require('crypto');

// corpo: o corpo da requisição exatamente como chegou (texto, antes do JSON.parse)
function assinaturaValida(corpo, headers, segredo) {
  const timestamp = headers['x-planalto-timestamp'];
  const recebida = headers['x-planalto-assinatura'] || '';
  const esperada = 'sha256=' + crypto
    .createHmac('sha256', segredo)
    .update(timestamp + '.' + corpo)
    .digest('hex');
  const recente = Math.abs(Date.now() / 1000 - Number(timestamp)) < 300;
  return recente
    && recebida.length === esperada.length
    && crypto.timingSafeEqual(Buffer.from(recebida), Buffer.from(esperada));
}`;

export const verificacaoPython = `import hashlib, hmac, time

# corpo: o corpo da requisição exatamente como chegou (bytes)
def assinatura_valida(corpo: bytes, headers, segredo: str) -> bool:
    timestamp = headers["X-Planalto-Timestamp"]
    recebida = headers.get("X-Planalto-Assinatura", "")
    esperada = "sha256=" + hmac.new(
        segredo.encode(), timestamp.encode() + b"." + corpo, hashlib.sha256
    ).hexdigest()
    recente = abs(time.time() - int(timestamp)) < 300
    return recente and hmac.compare_digest(recebida, esperada)`;
